<script lang="ts">
	import type { PageData } from './$types';
	import {
		getBrancheFromCollections,
		getProductIdFromShopifyUri,
		getVariantIdFromShopifyUri,
		isProd
	} from '$lib/utils';
	import Breadcrumbs from '$lib/components/Breadcrumbs.svelte';
	import MobileImageViewer from '$lib/components/Product/MobileImageViewer.svelte';
	import ProductInfo from '$lib/components/Product/ProductInfo.svelte';
	import ProductFormAddToCart from '$lib/components/Product/ProductFormAddToCart.svelte';
	import { page } from '$app/stores';
	import DeliveryIndicator from '$lib/components/Product/DeliveryIndicator.svelte';
	import ProductInfoList from '$lib/components/Product/ProductInfoList.svelte';
	import { StoryblokComponent } from '@storyblok/svelte';
	import { getTranslate } from '@tolgee/svelte';
	import ProductInformationButton from '$lib/components/Product/ProductInformationButton.svelte';
	import ProductBundleRecommendation from '$lib/components/Product/SpecialSale/ProductBundleRecommendation.svelte';
	import OutOfStockDrawer from '$lib/components/Drawer/Product/OutOfStockDrawer.svelte';
	import { browser } from '$app/environment';
	import { afterNavigate, goto } from '$app/navigation';
	import {
		getStructuredData,
		mapToNode,
		transformProductCollectionsToBranch
	} from '$lib/utils/product';
	import { parseImageUrls } from '$lib/utils/image';
	import { getCollectionPath } from '$lib/utils/collectionTree';
	import { getGtmPayloadProductViewed } from '$lib/gtm/productDetails';
	import type { ProductDetails } from '$lib/gtm/types';
	import { gtmDispatch } from '$lib/gtm/gtmDispatch';
	import { resolveRoute } from '$app/paths';
	import slug from 'slug';
	import ProductCrossSellProducts from '$lib/components/Product/ProductCrossSellProducts.svelte';
	import { tick } from 'svelte';

	export let data: PageData;

	$: sdsVariant = mapToNode(data.sdsProduct?.variants).find(
		(variant) => variant.sku?.value === data.product.variant.sku!.value
	);
	$: showJdm = false;
	$: avantradoVariant = data.product?.variant;
	$: productImageUrls = parseImageUrls(data.product?.image_urls?.value);
	$: structuredData = getStructuredData(
		data.product,
		data.product?.variant,
		sdsVariant,
		productImageUrls || [],
		$page.params.locale,
		$page.params.country,
		$page.params.branch
	);
	$: collectionPath = getCollectionPath(data.product.collections);
	$: branch =
		collectionPath[0]?.url_key ||
		transformProductCollectionsToBranch(data.product.collections) ||
		'hund';

	const { t } = getTranslate();

	$: metaDefaultDescription = data?.product
		? $t('meta.default.description.products', { product: data.product?.title })
		: '';

	// Send gtm event when product is loaded.
	$: {
		if (data.product?.title && sdsVariant && browser) {
			const productDetailsPayload: { productDetails: ProductDetails } = {
				productDetails: {
					availability: data.product?.availableForSale ? 'in_stock' : 'out_of_stock',
					branche: getBrancheFromCollections(data.product?.collections),
					price_max: data.product?.priceRange.maxVariantPrice.amount ?? 0,
					price_min: data.product?.priceRange.minVariantPrice.amount ?? 0,
					product_id: getProductIdFromShopifyUri(data.product.id),
					product_variant_id_avantrado: getVariantIdFromShopifyUri(data.product?.variant?.id) ?? '',
					product_variant_id_sds: getVariantIdFromShopifyUri(sdsVariant.id ?? '') ?? '',
					product_category: data.product?.collections.edges[0]?.node.title ?? ''
				}
			};

			const gtmPayload = getGtmPayloadProductViewed(productDetailsPayload);
			gtmDispatch(gtmPayload);
		}
	}
	$: imageData = {
		variantImageUrls: data.product.variant.image_urls,
		variantThreeDPreview: data.product.variant.dreid,
		variantVideoId: data.product.variant.video,
		productImageUrls: data.product.image_urls,
		productThreeDPreview: data.product.dreid,
		productVideoId: data.product.video
	};
	afterNavigate(async (navigation) => {
		if (navigation.from?.params?.handle === navigation.to?.params?.handle) {
			return;
		}
		const imageDataTmp = { ...imageData };
		imageData = {
			variantImageUrls: null,
			variantThreeDPreview: null,
			variantVideoId: null,
			productImageUrls: null,
			productThreeDPreview: null,
			productVideoId: null
		};
		showJdm = false;
		await tick();
		showJdm = true;
		imageData = imageDataTmp;
		await tick();
		window.jdgmCacheServer?.reloadAll();
	});

	$: if (
		browser &&
		($page.params.branch !== branch || $page.params.slug !== slug(data.product.title)) &&
		$page.params.handle === data.product.handle // DO NOT REMOVE: This is to prevent infinite redirects
	) {
		goto(
			resolveRoute($page.route.id || '', {
				...$page.params,
				branch: branch,
				slug: slug(data.product.title)
			}),
			{
				replaceState: true,
				state: $page.state,
				noScroll: true,
				keepFocus: true
			}
		);
	}
</script>

<svelte:head>
	{#if data?.product && data?.product?.variant && sdsVariant}
		<title
			>{data.product?.metaTitle?.value
				? data.product.metaTitle?.value
				: `${data.product.title} | Sprenger`}</title
		>
		<meta
			name="description"
			content={data.product?.metaDescription?.value
				? data.product?.metaDescription?.value
				: metaDefaultDescription}
		/>
		<meta
			property="og:title"
			content={`${data.product.title} ${data.product.variant?.title ?? ''}`.trim()}
		/>
		{#if data.product?.description}
			<meta property="og:description" content={data.product.description} />
		{/if}
		<meta property="og:site_name" content="Sprenger Shop" />
		{#if productImageUrls}
			<meta property="og:image" content={`/api/og?original_url=${productImageUrls[0]}`} />
			<meta property="og:image:url" content={`/api/og?original_url=${productImageUrls[0]}`} />
		{/if}
		{#if structuredData}
			<!-- eslint-disable-next-line svelte/no-at-html-tags -->
			{@html structuredData}
		{/if}
		{#if isProd()}
			<script>
				jdgm = window.jdgm || {};
				jdgm.SHOP_DOMAIN = 'hs-sprenger.myshopify.com';
				jdgm.PLATFORM = 'shopify';
				jdgm.PUBLIC_TOKEN = 'zpnOOxOjcW80yUKkaLhe38QSXQo';
			</script>
		{:else}
			<script>
				jdgm = window.jdgm || {};
				jdgm.SHOP_DOMAIN = 'sprenger-store.myshopify.com';
				jdgm.PLATFORM = 'shopify';
				jdgm.PUBLIC_TOKEN = 'r5167DE40khXVD2CHPIkNT_9hiw';
			</script>
		{/if}
		<script
			data-cfasync="false"
			type="text/javascript"
			defer
			src="https://cdn1.judge.me/assets/installed.js"
		></script>
		<script
			data-cfasync="false"
			type="text/javascript"
			src="https://cdn.judge.me/widget_preloader.js"
		></script>
	{/if}
</svelte:head>

{#if data?.product && data?.product?.variant && sdsVariant}
	<main class="box-x-wrapper">
		<Breadcrumbs {collectionPath} class="mb-8" />
		<div class="grid md:grid-cols-3 gap-x-5">
			<div class="md:col-span-2">
				<MobileImageViewer {...imageData} />
			</div>
			<div>
				<ProductInfo product={data.product} variant={avantradoVariant} {showJdm} />
				<ProductFormAddToCart
					options={data.product.options}
					lightVariants={data.product.variants}
					colormap={data.product.colormap}
					{sdsVariant}
					variant={avantradoVariant}
					class="my-2 space-y-4"
					isSingle={data.product.is_single?.value === 'true'}
					hasSizeData={!!(
						data.product.measurement_table?.value &&
						data.product.measurement_table?.value !== 'null'
					)}
				/>
				<DeliveryIndicator {sdsVariant} variant={avantradoVariant} />
				<ProductBundleRecommendation product={data.product} />
				<ProductInfoList product={data.product} variant={avantradoVariant} />
			</div>
		</div>

		<div class="w-full pt-12">
			<div class="flex flex-col mt-8 md:flex-row">
				<div class="flex flex-col items-start justify-center flex-1 mr-8 md:w-4/6">
					<h2 class="mb-2 font-semibold uppercase text-h-24 md:text-h-32">
						{$t('product.information')}
					</h2>
					<p>
						<!-- eslint-disable-next-line svelte/no-at-html-tags -->
						{@html data.product?.descriptionHtml}
					</p>
				</div>

				{#await data.storyblokData}
					<div class="flex flex-col md:w-2/6 gap-2 pt-8 pl-0 mb-1 md:pl-4">
						<div class="w-full h-12 animate-pulse bg-gray-400"></div>
						<div class="w-full h-12 animate-pulse bg-gray-400"></div>
						<div class="w-full h-12 animate-pulse bg-gray-400"></div>
					</div>
				{:then storyblokData}
					{#if storyblokData}
						<ProductInformationButton variant={data.product.variant} {storyblokData} />
					{/if}
				{/await}
			</div>
		</div>
	</main>

	<div class="box-x-wrapper">
		{#if showJdm}
			<div
				class="jdgm-widget jdgm-review-widget jdgm-outside-widget"
				data-id={data.product.id.replace('gid://shopify/Product/', '')}
				data-product-title={data.product.title}
			/>
		{/if}
	</div>
	{#await data.storyblokData then storyblokData}
		{#if storyblokData}
			<div class="w-full pb-8">
				{#each storyblokData.product.length > 0 ? storyblokData.product : storyblokData.variant as story}
					<StoryblokComponent blok={story.content} />
				{/each}
			</div>
		{/if}
	{/await}

	<ProductCrossSellProducts
		variantCrossSellReferences={data.product.variant.crossSellReferences}
		productCrossSellReferences={data.product.crossSellReferences}
	/>

	<OutOfStockDrawer />
{/if}
